<script setup lang="ts">
import type { EventInfo } from '@/types/event';
import type { TranslatableEvent } from '@/types/translation';

const props = defineProps({
	data: {
		type: Object as PropType<EventInfo>,
		required: false,
		default: null,
	},
	translatableData: {
		type: Object as PropType<TranslatableEvent>,
		required: false,
		default: null,
	},
	isTranslated: {
		type: Boolean,
		required: false,
		default: false,
	},
	trackingSection: {
		type: String,
		required: false,
		default: '',
	},
	isHostingUserProfile: {
		type: Boolean,
		required: false,
	},
	hideDivider: {
		type: Boolean,
		default: false,
	},
});

const { trackEvent } = useGtm();
const profileStore = useProfileStore();

function getDisplay(field: any, fallback: any) {
	if (!field) return fallback;
	if (typeof field === 'object' && 'original' in field) {
		return props.isTranslated ? field.translated || field.original : field.original;
	}
	return field;
}

function handleClick() {
	trackEvent({
		event: GTM_EVENT_NAME.VIEW_FEATURED_EVENT,
		event_id: (props.translatableData || props.data)?.id,
		user_id: profileStore.profileId,
		section: props.trackingSection || null,
	});
}

const eventRoute = computed(() => {
	const eventData = props.translatableData || props.data;
	const eventId = eventData?.id;

	// If viewing from hosting list, route to management interface
	if (props.isHostingUserProfile && eventId) {
		// For recurring events (isEventSeries), route to preview mode (edit is blocked in Peasy)
		// For regular events, route to edit page
		const routeName = eventData.isEventSeries ? ROUTE.EVENT_PREVIEW.NAME : ROUTE.EVENT_EDIT.NAME;
		return {
			name: routeName,
			params: { id: eventId },
		};
	}

	// Default: route to public view
	return {
		name: ROUTE.EVENT_VIEW.NAME,
		params: { id: eventId },
	};
});
</script>

<template>
	<RouterLink
		v-if="(translatableData || data)?.id"
		:to="eventRoute"
		target="_blank"
		rel="noopener noreferrer"
		@click="handleClick"
	>
		<div class="event-detail-item">
			<div class="cover-image">
				<img
					v-if="(translatableData || data)?.coverImageUrl"
					:src="(translatableData || data)?.coverImageUrl"
				>
				<div
					v-else
					class="placeholder"
				>
					<PeatixGrayIcon />
				</div>
			</div>
			<div class="detail">
				<p
					v-if="(translatableData || data)?.displayDate && (translatableData || data)?.displayTime"
					class="headline text-left ellipsis"
				>
					<span>{{ `${(translatableData || data)?.displayDate} ` }}</span>
					<span>&middot;&nbsp;</span>
					<span>{{ `${(translatableData || data)?.displayTime} ` }}</span>
					<span>{{ (translatableData || data)?.displayTimezone }}</span>
				</p>
				<p class="title text-wrap text-left ellipsis">
					{{ getDisplay((translatableData || data)?.name, (translatableData || data)?.name) }}
				</p>
				<slot name="moreDetail" />
				<p class="sub-title text-left ellipsis">
					by
					{{
						getDisplay((translatableData || data)?.groupName, (translatableData || data)?.groupName)
					}}
				</p>
				<p class="sub-title text-left ellipsis">
					<template v-if="(translatableData || data)?.locationType === EVENT_TYPE.PHYSICAL">
						<template v-if="(translatableData || data)?.city && (translatableData || data)?.state">
							{{ getDisplay((translatableData || data)?.city, (translatableData || data)?.city) }},
							{{ getDisplay((translatableData || data)?.state, (translatableData || data)?.state) }}
						</template>
						<template v-else>
							{{
								getDisplay((translatableData || data)?.address, (translatableData || data)?.address)
							}}
						</template>
					</template>
					<template v-else>
						Online event
					</template>
				</p>
				<slot name="eventStatus" />
			</div>
		</div>
	</RouterLink>
</template>

<style lang="scss" scoped>
$image-cover-height: rem(136);

.event-detail-item {
	position: relative;
	min-height: rem(280);
	max-width: rem(320);
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: spacings-get(3);
	box-shadow:
		0px 4px 8px -2px rgba(16, 24, 40, 0.1),
		0px 2px 4px -2px rgba(16, 24, 40, 0.06);
	background-color: white;
	border-radius: rem(6);

	@include media-query-max(mobile) {
		max-width: none;
		height: unset;
	}

	.cover-image {
		width: 100%;
		height: $image-cover-height;
		position: absolute;
		left: 0;
		top: 0;

		.placeholder {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
			background-color: colors-get(gray, 200);
		}

		img {
			width: 100%;
			height: $image-cover-height;
			object-fit: cover;
			object-position: top;
		}

		img,
		.placeholder {
			border-radius: spacings-get(2) spacings-get(2) 0 0;
		}

		.placeholder {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
			background-color: colors-get(gray, 200);
			border-radius: rem(4);
		}

		@include media-query-max(mobile) {
			img,
			.placeholder {
				height: 100%;
			}
		}
	}

	.detail {
		position: relative;
		margin-top: $image-cover-height;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: spacings-get(1);
		padding: spacings-get(4);

		:deep(.base-chip) {
			height: auto;
		}

		.title {
			text-decoration: initial;
			color: colors-get(gray, 900);
			@include fonts-get(medium, text-sm);
			-webkit-line-clamp: 2;
		}

		.sub-title {
			color: colors-get(gray, 500);
			@include fonts-get(regular, text-xs);
			-webkit-line-clamp: 1;
		}

		.headline {
			color: colors-get(primary, 600);
			@include fonts-get(regular, text-xs);
			-webkit-line-clamp: 2;
		}
	}
}
</style>
